import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Alert,
  ActivityIndicator,
  Image,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirestore, collection, addDoc, getDocs, where, serverTimestamp, doc, updateDoc, query, deleteDoc, orderBy, limit } from 'firebase/firestore';
import * as DocumentPicker from 'expo-document-picker';
import { useUser } from '../components/LoginPersistence';
import { useCommunity } from '../contexts/CommunityContext';
import EditablePostGrid from '../components/EditablePostGrid';
import { MoveModeDesktopNote } from '../components/MoveModeDesktopNote';
import { CardBgColorPicker } from '../components/CardBgColorPicker';
import { DismissOverlay } from '../components/DismissOverlay';
import { InterestEmojiStrip } from '../components/InterestEmojiStrip';
import { useInterestCatalog } from '../hooks/useInterestCatalog';
import { resolveInterestDisplayItems } from '../utils/interestUtils';
import { transparentTextInputStyle } from '../utils/transparentTextInputStyle';
import { getResponsiveValue } from '../styles/breakpoints';
import {
  buildGridFromItems,
  getRowHeightMultipliers,
  GRID_COLS,
  GRID_ROWS,
  EMPTY_WIDGET_TYPE,
} from '../utils/postGridEngine';

export { buildGridFromItems, getRowHeightMultipliers, GRID_COLS, GRID_ROWS };

const COMMUNITY_BLUE = '#1075ae';
const ACCENT = '#fbcd9d';
export const CELL_SIZE = getResponsiveValue({
  mobile: 100,
  tablet: 110,
  laptop: 120,
  desktop: 120
});
export const WIDGET_TYPES = [
  { id: 'text', label: 'Text', icon: 'document-text-outline' },
  { id: 'file', label: 'Media', icon: 'image-outline' },
  { id: 'audio', label: 'Audio', icon: 'musical-notes-outline' },
  { id: 'map', label: 'Map', icon: 'map-outline' },
  { id: 'link', label: 'Link', icon: 'link-outline' },
  { id: 'calendar', label: 'Calendar', icon: 'calendar-outline' },
  // { id: 'message', label: 'Message', icon: 'chatbubble-outline' },
];

export const CARD_BG_SWATCHES = [
  // Neutrals
  '#ffffff', '#f5f0e8', '#e8e8e8', '#1a1a1a', '#0f172a',
  // Pastels
  '#feffe4', '#fef9c3', '#dcfce7', '#dbeafe', '#ede9fe',
  '#fce7f3', '#ffedd5', '#f0fdf4', '#ecfeff', '#fff1f2',
  // Vibrant
  '#1e3a5f', '#1a472a', '#4a1942', '#7c2d12', '#1c1917',
  // Brand-ish
  '#1075ae', '#2d6a4f', '#6d28d9', '#dc2626', '#d97706',
];

const parseCalendarWidgetValue = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_) {
    return { date: raw };
  }
  return null;
};

const enumerateDateKeysInclusive = (startKey, endKey) => {
  const out = [];
  const start = new Date(`${startKey}T00:00:00`);
  const end = new Date(`${endKey}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return out;
  const cursor = new Date(start);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = `${cursor.getMonth() + 1}`.padStart(2, '0');
    const d = `${cursor.getDate()}`.padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

const extractCalendarRangesFromItems = (items) => {
  const ranges = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (item?.type !== 'calendar') return;
    const parsed = parseCalendarWidgetValue(item.value);
    const startDate = parsed?.startDate || parsed?.date;
    const endDate = parsed?.endDate || startDate;
    if (!startDate) return;
    ranges.push({
      widgetId: item.id,
      startDate,
      endDate,
      title: parsed?.title || '',
    });
  });
  return ranges;
};

export const sanitizeForFirestore = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeForFirestore(entry))
      .filter((entry) => entry !== undefined);
  }
  if (typeof value === 'object') {
    const sanitized = {};
    Object.entries(value).forEach(([key, nested]) => {
      const cleaned = sanitizeForFirestore(nested);
      if (cleaned !== undefined) sanitized[key] = cleaned;
    });
    return sanitized;
  }
  return value;
};

export function buildCommunityDefaultTemplate() {
  return [
    { id: 'cell-0', type: 'file', value: '', x: 0, y: 0, w: 3, h: 2 },
    { id: 'cell-1', type: EMPTY_WIDGET_TYPE, value: '', x: 0, y: 2, w: 1, h: 1 },
    { id: 'cell-2', type: 'calendar', value: '', x: 1, y: 2, w: 1, h: 1, cellBorderRadiusPercent: 100 },
    { id: 'cell-3', type: EMPTY_WIDGET_TYPE, value: '', x: 2, y: 2, w: 1, h: 1 },
    { id: 'cell-4', type: 'map', value: '', x: 0, y: 3, w: 3, h: 1 },
    { id: 'cell-5', type: EMPTY_WIDGET_TYPE, value: '', x: 0, y: 4, w: 1, h: 1 },
    { id: 'cell-6', type: EMPTY_WIDGET_TYPE, value: '', x: 1, y: 4, w: 1, h: 1 },
    { id: 'cell-7', type: EMPTY_WIDGET_TYPE, value: '', x: 2, y: 4, w: 1, h: 1 },
  ];
}

export const TEMPLATES = [
  {
    id: 'community',
    label: 'Community',
    icon: 'newspaper-outline',
    build: buildCommunityDefaultTemplate,
  },
  {
    id: 'blank',
    label: 'Blank',
    icon: 'grid-outline',
    build: () => Array.from({ length: 15 }, (_, i) => ({
      id: `cell-${i}`, type: EMPTY_WIDGET_TYPE, value: '',
      x: i % 3, y: Math.floor(i / 3), w: 1, h: 1,
    })),
  },
  {
    id: 'header',
    label: 'Header',
    icon: 'albums-outline',
    build: () => [
      { id: 'cell-0', type: 'text', value: '', x: 0, y: 0, w: 3, h: 1 },
      ...Array.from({ length: 12 }, (_, i) => ({
        id: `cell-${i + 1}`, type: 'text', value: '',
        x: i % 3, y: Math.floor(i / 3) + 1, w: 1, h: 1,
      })),
    ],
  },
  {
    id: 'portrait',
    label: 'Portrait',
    icon: 'phone-portrait-outline',
    build: () => [
      { id: 'cell-0', type: 'file', value: '', x: 0, y: 0, w: 1, h: 3 },
      ...Array.from({ length: 6 }, (_, i) => ({
        id: `cell-${i + 1}`, type: 'text', value: '',
        x: (i % 2) + 1, y: Math.floor(i / 2), w: 1, h: 1,
      })),
      ...Array.from({ length: 6 }, (_, i) => ({
        id: `cell-${i + 7}`, type: 'text', value: '',
        x: i % 3, y: Math.floor(i / 3) + 3, w: 1, h: 1,
      })),
    ],
  },
  {
    id: 'feature',
    label: 'Feature',
    icon: 'expand-outline',
    build: () => [
      { id: 'cell-0', type: 'file', value: '', x: 0, y: 0, w: 2, h: 2 },
      { id: 'cell-1', type: 'text', value: '', x: 2, y: 0, w: 1, h: 1 },
      { id: 'cell-2', type: 'text', value: '', x: 2, y: 1, w: 1, h: 1 },
      ...Array.from({ length: 9 }, (_, i) => ({
        id: `cell-${i + 3}`, type: 'text', value: '',
        x: i % 3, y: Math.floor(i / 3) + 2, w: 1, h: 1,
      })),
    ],
  },
  {
    id: 'banner',
    label: 'Banner',
    icon: 'image-outline',
    build: () => [
      { id: 'cell-0', type: 'file', value: '', x: 0, y: 0, w: 3, h: 2 },
      ...Array.from({ length: 9 }, (_, i) => ({
        id: `cell-${i + 1}`, type: 'text', value: '',
        x: i % 3, y: Math.floor(i / 3) + 2, w: 1, h: 1,
      })),
    ],
  },
];

/** Return true if the grid item has user content (filled). */
export function isGridCellFilled(item) {
  if (!item) return false;
  if (item.type === EMPTY_WIDGET_TYPE) return false;
  const v = item.value;
  if (v != null && String(v).trim() !== '') return true;
  if (item.type === 'file' && v) {
    try {
      const parsed = JSON.parse(v);
      if (parsed?.url) return true;
    } catch (_) {}
  }
  return false;
}

/**
 * Merge current grid items into a new template layout. Filled cells get priority:
 * we assign existing content to new slots in order (filled first, then by position).
 * New layout's structure (x, y, w, h, id) is kept; type and value come from current items when available.
 * Extra current items (no slot in new layout) are dropped.
 */
export function mergeTemplateWithCurrent(currentItems, newTemplateItems) {
  const current = (currentItems || []).slice();
  const newSlots = (newTemplateItems || []).slice().sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
  const currentSorted = current.sort((a, b) => {
    const fillA = isGridCellFilled(a) ? 1 : 0;
    const fillB = isGridCellFilled(b) ? 1 : 0;
    if (fillB !== fillA) return fillB - fillA;
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });
  return newSlots.map((slot, i) => {
    if (i < currentSorted.length) {
      const cur = currentSorted[i];
      return {
        ...slot,
        type: cur.type,
        value: cur.value,
        cellBg: cur.cellBg,
        cellBorderRadius: cur.cellBorderRadius,
        cellBorderRadiusPercent: cur.cellBorderRadiusPercent,
        fontSize: cur.fontSize,
        textAlign: cur.textAlign,
        textColor: cur.textColor,
        fontFamily: cur.fontFamily,
        linkDisplayMode: cur.linkDisplayMode,
        linkButtonLabel: cur.linkButtonLabel,
        linkButtonBg: cur.linkButtonBg,
        linkButtonFg: cur.linkButtonFg,
        linkButtonShape: cur.linkButtonShape,
        linkButtonSize: cur.linkButtonSize,
      };
    }
    return { ...slot };
  });
}

// Build "Post As" summary: "<column header>, <value>" per category (e.g. "PERFORMER, Comedian")
function buildPostSummary(postSelected, postCustom) {
  const parts = [];
  const eventVal = postCustom?.event || postSelected?.event || '';
  if (eventVal) parts.push(`EVENT, ${eventVal}`);
  const crewVal = postCustom?.crew || postSelected?.crew || '';
  if (crewVal) parts.push(`CREW, ${crewVal}`);
  const performerVal = postCustom?.performer || postSelected?.performer || '';
  if (performerVal) parts.push(`PERFORMER, ${performerVal}`);
  const resourceVal = postCustom?.resource || postSelected?.resource || '';
  if (resourceVal) parts.push(`RESOURCE, ${resourceVal}`);
  return parts.join(' · ') || '';
}

export default function CommunityPostCreate() {
  const route = useRoute();
  const navigation = useNavigation();
  const { user: userData } = useUser();
  const { refreshEntities } = useCommunity();
  const { interests: interestCatalog } = useInterestCatalog();
  const authorInterestItems = useMemo(
    () => resolveInterestDisplayItems(userData?.interests || [], interestCatalog),
    [userData?.interests, interestCatalog]
  );
  const { postSelected = {}, postCustom = {}, postAs = {}, editPostId = null, existingPost = null } = route.params || {};
  const [posting, setPosting] = useState(false);
  const [adminOnly, setAdminOnly] = useState(existingPost?.adminOnly ?? false);
  const isUserAdmin = userData?.userType === 'admin';
  const [venueSuggestions, setVenueSuggestions] = useState([]);
  const [showVenueSuggestions, setShowVenueSuggestions] = useState(false);

  const isEditing = !!editPostId;

  const initialSummary = useMemo(
    () => (isEditing ? '' : buildPostSummary(postSelected, postCustom)),
    [isEditing, postSelected, postCustom]
  );

  // Lock the label part ("EVENT, ", "CREW, " etc.) so only the value after the comma is editable.
  const lockedSummarySegments = useMemo(() => {
    if (isEditing) return null;
    if (!initialSummary) return null;
    const segments = String(initialSummary).split(' · ');
    return segments.map(seg => {
      const idx = seg.indexOf(', ');
      if (idx === -1) {
        return { prefix: seg, suffix: '' };
      }
      return {
        prefix: seg.slice(0, idx + 2), // include ", "
        suffix: seg.slice(idx + 2)
      };
    });
  }, [isEditing, initialSummary]);
  const [postContent, setPostContent] = useState(
    existingPost?.postContent ?? initialSummary
  );
  const [postContentParts, setPostContentParts] = useState(
    lockedSummarySegments ? lockedSummarySegments.map(seg => seg.suffix) : []
  );

  // When we first have a locked summary, initialize the inputs after the comma
  // with the suffix values (but don't overwrite user edits once they exist).
  useEffect(() => {
    if (!lockedSummarySegments || lockedSummarySegments.length === 0) return;

    const hasUserEdits = postContentParts.some(val => val && String(val).trim().length > 0);
    if (hasUserEdits) return;

    setPostContentParts(lockedSummarySegments.map(seg => seg.suffix));
  }, [lockedSummarySegments]);

  // Keep postContent in sync with the per-segment values when we have a locked summary.
  useEffect(() => {
    if (!lockedSummarySegments || lockedSummarySegments.length === 0) return;
    setPostContent(
      lockedSummarySegments
        .map((seg, index) => `${seg.prefix}${postContentParts[index] ?? ''}`)
        .join(' · ')
    );
  }, [lockedSummarySegments, postContentParts]);

  // Sync postContent when navigating with new params (e.g. from POST modal)
  // Only apply auto-summary if the field is currently empty, so we don't stack/duplicate roles.
  useEffect(() => {
    if (isEditing) return;
    setPostContent(prev => {
      if (prev && String(prev).trim().length > 0) return prev;
      const summary = buildPostSummary(route.params?.postSelected ?? {}, route.params?.postCustom ?? {});
      return summary || prev;
    });
  }, [isEditing, route.params?.postSelected, route.params?.postCustom]);
  const [location, setLocation] = useState(existingPost?.location ?? '');

  const initialItems = useMemo(() => buildCommunityDefaultTemplate(), []);
  const [items, setItems] = useState(
    existingPost?.items && Array.isArray(existingPost.items) ? existingPost.items : initialItems
  );
  const [cardBackground, setCardBackground] = useState(existingPost?.cardBackground ?? null);
  const [headerTextColor, setHeaderTextColor] = useState(existingPost?.headerTextColor ?? null);
  const [cardBgPanelOpen, setCardBgPanelOpen] = useState(false);
  const [cardBgTab, setCardBgTab] = useState('color'); // 'color' | 'image' | 'video'
  const [showInterestsOnPost, setShowInterestsOnPost] = useState(existingPost?.showInterestsOnPost !== false);
  const [uploadingCardBg, setUploadingCardBg] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [gridMode, setGridMode] = useState('move');
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [historyVersion, setHistoryVersion] = useState(0);

  const [uploadingCellId, setUploadingCellId] = useState(null);

  const closePanels = useCallback(() => {
    setShowTemplates(false);
    setCardBgPanelOpen(false);
  }, []);

  const handleFileUpload = useCallback(async (id, cellType = 'file') => {
    try {
      setUploadingCellId(id);
      const acceptTypes = cellType === 'audio'
        ? ['audio/*']
        : ['image/*', 'video/*', 'audio/*', 'application/pdf'];
      const result = await DocumentPicker.getDocumentAsync({
        type: acceptTypes,
        copyToCacheDirectory: true,
      });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        const response = await fetch(file.uri);
        const fileBlob = await response.blob();
        const storage = getStorage();
        const uid = userData?.uid || 'anonymous';
        const storageRef = ref(storage, `communityPosts/${uid}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, fileBlob);
        const downloadURL = await getDownloadURL(storageRef);
        let mimeType = file.mimeType;
        if (!mimeType) {
          if (/\.(mp4|webm|mov|avi|m4v)$/i.test(file.name || '')) mimeType = 'video/mp4';
          else if (/\.(mp3|wav|ogg|aac|flac|m4a)$/i.test(file.name || '')) mimeType = 'audio/mpeg';
          else if (/\.pdf$/i.test(file.name || '')) mimeType = 'application/pdf';
          else mimeType = 'image/*';
        }
        const value = JSON.stringify({ url: downloadURL, name: file.name, mimeType });
        setItemsWithHistory(prev => prev.map(i => i.id === id ? { ...i, value } : i));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      Alert.alert('Upload failed', 'Failed to upload file. Please try again.');
    } finally {
      setUploadingCellId(null);
    }
  }, [userData?.uid]);

  const setItemsWithHistory = useCallback((updater) => {
    setItems((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      undoStackRef.current.push(prev);
      if (undoStackRef.current.length > 50) undoStackRef.current.shift();
      redoStackRef.current = [];
      setHistoryVersion((v) => v + 1);
      return next;
    });
  }, []);

  const canUndo = undoStackRef.current.length > 0;
  const canRedo = redoStackRef.current.length > 0;

  const handleUndo = useCallback(() => {
    setItems((current) => {
      const previous = undoStackRef.current.pop();
      if (!previous) return current;
      redoStackRef.current.push(current);
      setHistoryVersion((v) => v + 1);
      return previous;
    });
  }, []);

  const handleRedo = useCallback(() => {
    setItems((current) => {
      const next = redoStackRef.current.pop();
      if (!next) return current;
      undoStackRef.current.push(current);
      setHistoryVersion((v) => v + 1);
      return next;
    });
  }, []);

  const handleCardBgUpload = useCallback(async (bgType) => {
    try {
      setUploadingCardBg(true);
      const acceptTypes = bgType === 'video' ? ['video/*'] : ['image/*'];
      const result = await DocumentPicker.getDocumentAsync({ type: acceptTypes, copyToCacheDirectory: true });
      if (!result.canceled && result.assets?.length > 0) {
        const file = result.assets[0];
        const response = await fetch(file.uri);
        const fileBlob = await response.blob();
        const storage = getStorage();
        const uid = userData?.uid || 'anonymous';
        const storageRef = ref(storage, `communityPostBg/${uid}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, fileBlob);
        const url = await getDownloadURL(storageRef);
        setCardBackground({ type: bgType, value: url });
      }
    } catch (e) {
      Alert.alert('Upload failed', 'Could not upload background.');
    } finally {
      setUploadingCardBg(false);
    }
  }, [userData?.uid]);

  const handleSaveToFolder = useCallback(() => {
    Alert.alert('Save to device', 'This would save the post to your file manager. (Not implemented in this build.)');
  }, []);

  const syncCalendarEventsForPost = useCallback(async ({ db, postId, postTitle, postLocation, postItems }) => {
    // NOTE: Temporarily disabled per product request.
    // Community Directory post calendar widgets should NOT auto-sync to Tour/My Calendar.
    // Keep the old implementation commented for likely near-term reimplementation.
    /*
    if (!db || !postId || !userData?.uid) return;

    const existingQ = query(
      collection(db, 'users', userData.uid, 'calendarEvents'),
      where('communityPostId', '==', postId)
    );
    const existingSnap = await getDocs(existingQ);
    await Promise.all(
      existingSnap.docs
        .filter((d) => d.data()?.autoCreatedFromPost === true)
        .map((d) => deleteDoc(d.ref))
    );

    const ranges = extractCalendarRangesFromItems(postItems);
    if (!ranges.length) return;

    const targetCollection = collection(db, 'users', userData.uid, 'calendarEvents');
    for (let i = 0; i < ranges.length; i += 1) {
      const range = ranges[i];
      const dayKeys = enumerateDateKeysInclusive(range.startDate, range.endDate);
      for (const dateKey of dayKeys) {
        await addDoc(targetCollection, {
          title: range.title || postTitle || 'Community post',
          date: dateKey,
          notes: postLocation || '',
          color: '#5b7fff',
          communityPostId: postId,
          communityPostTitle: postTitle || '',
          eventType: 'community_post',
          sortOrder: i,
          autoCreatedFromPost: true,
          autoSourceWidgetId: range.widgetId || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }
    */
    return;
  }, [userData?.uid]);

  const handlePost = useCallback(async () => {
    if (!userData?.uid) {
      Alert.alert('Sign in required', 'Please sign in to post to the community.');
      return;
    }
    try {
      setPosting(true);
      const db = getFirestore();
      const sanitizedItems = sanitizeForFirestore(items);
      if (isEditing && editPostId) {
        await updateDoc(doc(db, 'communityPosts', editPostId), {
          postContent: postContent || '',
          location: location || '',
          items: sanitizedItems,
          cardBackground: cardBackground || null,
          headerTextColor: headerTextColor || null,
          adminOnly: adminOnly || false,
          showInterestsOnPost: showInterestsOnPost !== false,
          interests: userData?.interests || [],
          updatedAt: serverTimestamp(),
        });
        // NOTE: Temporarily disabled calendar auto-sync from community posts.
        // await syncCalendarEventsForPost({
        //   db,
        //   postId: editPostId,
        //   postTitle: postContent || '',
        //   postLocation: location || '',
        //   postItems: sanitizedItems,
        // });
      } else {
        // Enforce max 3 community listings per user
        // const q = query(
        //   collection(db, 'communityPosts'),
        //   where('userId', '==', userData.uid)
        // );
        // const existingSnap = await getDocs(q);
        // if (existingSnap.size >= 3) {
        //   Alert.alert(
        //     'Listing limit reached',
        //     'You can have up to 3 community listings at a time. Please delete one before creating a new listing.'
        //   );
        //   setPosting(false);
        //   return;
        // }
        const postRef = await addDoc(collection(db, 'communityPosts'), {
          userId: userData.uid,
          username: userData.username || null,
          photoURL: userData.photoURL || null,
          firstName: userData.firstName || null,
          lastName: userData.lastName || null,
          postContent: postContent || '',
          location: location || '',
          items: sanitizedItems,
          cardBackground: cardBackground || null,
          headerTextColor: headerTextColor || null,
          postSelected: postSelected || {},
          postCustom: postCustom || {},
          postAsType: postAs.type || 'user',
          postAsOrganizationId: postAs.organizationId || null,
          postAsOrganizationName: postAs.organizationName || null,
          adminOnly: adminOnly || false,
          showInterestsOnPost: showInterestsOnPost !== false,
          interests: userData?.interests || [],
          createdAt: serverTimestamp(),
        });
        // NOTE: Temporarily disabled calendar auto-sync from community posts.
        // await syncCalendarEventsForPost({
        //   db,
        //   postId: postRef.id,
        //   postTitle: postContent || '',
        //   postLocation: location || '',
        //   postItems: sanitizedItems,
        // });
      }
      refreshEntities?.();
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('CommunityList', { searchQuery: '' });
      }
    } catch (error) {
      console.error('Error posting:', error);
      Alert.alert('Post failed', 'Failed to publish. Please try again.');
    } finally {
      setPosting(false);
    }
  }, [userData, postContent, location, items, cardBackground, headerTextColor, postSelected, postCustom, refreshEntities, navigation, isEditing, editPostId, postAs.type, postAs.organizationId, postAs.organizationName, syncCalendarEventsForPost, showInterestsOnPost, adminOnly]);

  const handleLocationChange = useCallback(async (text) => {
    setLocation(text);
    if (!text || text.length < 2) {
      setVenueSuggestions([]);
      setShowVenueSuggestions(false);
      return;
    }
    try {
      const db = getFirestore();
      const lower = text.toLowerCase();
      // Fetch a limited set of venues ordered by name and filter client-side for prefix match
      const snap = await getDocs(
        query(collection(db, 'venues'), orderBy('name'), limit(200))
      );
      const matches = [];
      snap.forEach((d) => {
        const v = d.data();
        const name = (v.name || '').toLowerCase();
        const city = (v.city || '').toLowerCase();
        const state = (v.state || '').toLowerCase();
        if (
          name.includes(lower) ||
          city.includes(lower) ||
          state.includes(lower)
        ) {
          matches.push({
            id: d.id,
            label: [v.name, v.city, v.state].filter(Boolean).join(', '),
          });
        }
        if (matches.length >= 8) return;
      });
      setVenueSuggestions(matches.slice(0, 8));
      setShowVenueSuggestions(matches.length > 0);
    } catch (_) {}
  }, []);

  return (
    <View style={styles.container}>
      <DismissOverlay visible={showTemplates || cardBgPanelOpen} onDismiss={closePanels} />
      <View style={styles.header}>
        <TouchableOpacity onPress={() => {
          if (navigation.canGoBack()) {
            navigation.goBack();
          } else {
            navigation.navigate('CommunityList', { searchQuery: '' });
          }
        }} style={styles.backBtn} disabled={posting}>
          <Ionicons name="arrow-back" size={24} color={ACCENT} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Create post</Text>
      </View>

      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        {/* Single card: header inputs + Excel-style grid */}
        <View style={styles.card}>
          {/* Clipped background layer — overflow hidden so bg stays within rounded corners */}
          <View style={styles.cardBgLayer}>
            {cardBackground?.type === 'color' && cardBackground.value ? (
              <View style={{ flex: 1, backgroundColor: cardBackground.value }} />
            ) : null}
            {cardBackground?.type === 'image' && cardBackground.value ? (
              <Image source={{ uri: cardBackground.value }} style={{ flex: 1 }} resizeMode="cover" />
            ) : null}
            {cardBackground?.type === 'video' && cardBackground.value && Platform.OS === 'web' ? (
              <video src={cardBackground.value} autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            ) : null}
          </View>
          {/* Header row: post content, location, save */}
          <View style={[styles.previewRow, { zIndex: 1 }]}>
            <View style={styles.previewInputs}>
              {lockedSummarySegments && !isEditing ? (
                lockedSummarySegments.map((seg, index) => (
                  <View key={index} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                    <Text style={[styles.previewPostInput, headerTextColor ? { color: headerTextColor } : undefined, { marginRight: 4 }]}>
                      {seg.prefix}
                    </Text>
                    <TextInput
                      style={[styles.previewPostInput, headerTextColor ? { color: headerTextColor } : undefined, { flex: 1 }]}
                      placeholder="Value"
                      value={postContentParts[index] ?? ''}
                      onChangeText={(val) => {
                        setPostContentParts(prev => {
                          const next = [...prev];
                          next[index] = val;
                          return next;
                        });
                      }}
                      placeholderTextColor="#99999980"
                    />
                  </View>
                ))
              ) : (
                <TextInput
                  style={[styles.previewPostInput, headerTextColor ? { color: headerTextColor } : undefined]}
                  placeholder="Post as"
                  value={postContent}
                  onChangeText={setPostContent}
                  placeholderTextColor="#99999980"
                />
              )}
              <View style={{ position: 'relative', zIndex: 50 }}>
                <TextInput
                  style={[styles.previewLocationInput, headerTextColor ? { color: headerTextColor } : undefined]}
                  placeholder="Location"
                  value={location}
                  onChangeText={handleLocationChange}
                  onFocus={() => location.length >= 2 && setShowVenueSuggestions(venueSuggestions.length > 0)}
                  onBlur={() => setTimeout(() => setShowVenueSuggestions(false), 200)}
                  placeholderTextColor="#99999980"
                />
                {showVenueSuggestions && venueSuggestions.length > 0 && (
                  <View style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: '#fff',
                    borderRadius: 8,
                    shadowColor: '#000',
                    shadowOpacity: 0.12,
                    shadowRadius: 8,
                    elevation: 6,
                    zIndex: 100,
                    marginTop: 2,
                    overflow: 'hidden',
                  }}>
                    {venueSuggestions.map((v) => (
                      <TouchableOpacity
                        key={v.id}
                        onPress={() => {
                          setLocation(v.label);
                          setVenueSuggestions([]);
                          setShowVenueSuggestions(false);
                        }}
                        style={{ paddingVertical: 10, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' }}
                      >
                        <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 13, color: '#222' }}>{v.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
              {/* Header text color swatches */}
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 6 }}>
                <View style={{ flexDirection: 'row', gap: 5, alignItems: 'center' }}>
                  <Text style={{ fontSize: 10, color: '#999', marginRight: 2 }}>Aa</Text>
                  {[null, '#000', '#fff', '#333', '#f5f0e8', '#1075ae', '#fbcd9d', '#e74c3c', '#27ae60', '#8e44ad'].map((c, i) => (
                    <TouchableOpacity
                      key={i}
                      onPress={() => setHeaderTextColor(c)}
                      style={{
                        width: 16, height: 16, borderRadius: 8,
                        backgroundColor: c || 'transparent',
                        borderWidth: headerTextColor === c ? 2 : 1,
                        borderColor: headerTextColor === c ? COMMUNITY_BLUE : '#ccc',
                        alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {!c && <Text style={{ fontSize: 9, color: '#555' }}>✕</Text>}
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
            <View style={styles.previewActionsCol}>
              <TouchableOpacity onPress={handleSaveToFolder} style={styles.folderBtn}>
                <Ionicons name="folder-open" size={28} color={'#bca785'} />
              </TouchableOpacity>
              {showInterestsOnPost && authorInterestItems.length > 0 ? (
                <View style={styles.previewInterestStrip}>
                  <InterestEmojiStrip items={authorInterestItems} emojiSize={12} />
                </View>
              ) : null}
            </View>
          </View>

          {/* Card toolbar: Templates + Card Background */}
          <View style={styles.toolbar}>
            <View style={styles.toolbarLeft}>
              <TouchableOpacity
                style={[styles.toolbarBtn, showTemplates && { backgroundColor: COMMUNITY_BLUE }]}
                onPress={() => { setShowTemplates(p => !p); setCardBgPanelOpen(false); }}
              >
                <Ionicons name="grid-outline" size={16} color={showTemplates ? '#fff' : COMMUNITY_BLUE} />
                <Text style={[styles.toolbarBtnText, showTemplates && { color: '#fff' }]}>Templates</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.toolbarBtn, cardBgPanelOpen && { backgroundColor: COMMUNITY_BLUE }]}
                onPress={() => { setCardBgPanelOpen(p => !p); setShowTemplates(false); }}
              >
                <Ionicons name="color-palette-outline" size={16} color={cardBgPanelOpen ? '#fff' : COMMUNITY_BLUE} />
                <Text style={[styles.toolbarBtnText, cardBgPanelOpen && { color: '#fff' }]}>Card BG</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.toolbarBtn, showInterestsOnPost && { backgroundColor: COMMUNITY_BLUE }]}
                onPress={() => setShowInterestsOnPost((prev) => !prev)}
              >
                <Ionicons name="heart-outline" size={16} color={showInterestsOnPost ? '#fff' : COMMUNITY_BLUE} />
                <Text style={[styles.toolbarBtnText, showInterestsOnPost && { color: '#fff' }]}>Interests</Text>
              </TouchableOpacity>
              {cardBackground && (
                <TouchableOpacity style={styles.toolbarBtnClear} onPress={() => setCardBackground(null)}>
                  <Ionicons name="close-circle" size={16} color={COMMUNITY_BLUE} />
                  <Text style={styles.toolbarBtnText}>Clear BG</Text>
                </TouchableOpacity>
              )}
              {(showTemplates || cardBgPanelOpen) && (
                <TouchableOpacity style={styles.toolbarBtnClear} onPress={closePanels}>
                  <Ionicons name="chevron-up-circle" size={20} color={COMMUNITY_BLUE} />
                </TouchableOpacity>
              )}
            </View>
          </View>
          <View style={styles.modeRow}>
            <TouchableOpacity
              style={[styles.toolbarBtn, styles.modeAuxBtn, !canUndo && styles.modeAuxBtnDisabled]}
              onPress={handleUndo}
              disabled={!canUndo}
            >
              <Ionicons name="arrow-undo-outline" size={16} color={!canUndo ? '#9aa' : COMMUNITY_BLUE} />
              <Text style={[styles.toolbarBtnText, !canUndo && styles.modeAuxBtnTextDisabled]}>Undo</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toolbarBtn, styles.modeAuxBtn, !canRedo && styles.modeAuxBtnDisabled]}
              onPress={handleRedo}
              disabled={!canRedo}
            >
              <Ionicons name="arrow-redo-outline" size={16} color={!canRedo ? '#9aa' : COMMUNITY_BLUE} />
              <Text style={[styles.toolbarBtnText, !canRedo && styles.modeAuxBtnTextDisabled]}>Redo</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toolbarBtn, styles.modeBtn, gridMode === 'resize' && { backgroundColor: COMMUNITY_BLUE }]}
              onPress={() => setGridMode((m) => (m === 'move' ? 'resize' : 'move'))}
            >
              <Ionicons name={gridMode === 'move' ? 'move-outline' : 'resize-outline'} size={16} color={gridMode === 'resize' ? '#fff' : COMMUNITY_BLUE} />
              <Text style={[styles.toolbarBtnText, gridMode === 'resize' && { color: '#fff' }]}>
                {gridMode === 'move' ? 'Move Mode' : 'Resize Mode'}
              </Text>
            </TouchableOpacity>
          </View>
          <MoveModeDesktopNote />

          {/* Grid area: panels float above grid (absolute) */}
          <View style={styles.gridWithPanelsWrap}>
            {(showTemplates || cardBgPanelOpen) && (
              <View style={styles.panelsOverlay} pointerEvents="box-none">
                {showTemplates && (
                  <View style={styles.panel}>
                    <Text style={styles.panelTitle}>Choose a layout template</Text>
                    <View style={styles.templateRow}>
                      {TEMPLATES.map(t => (
                        <TouchableOpacity
                          key={t.id}
                          style={styles.templateBtn}
                          onPress={() => { setItemsWithHistory(prev => mergeTemplateWithCurrent(prev, t.build())); setShowTemplates(false); }}
                        >
                          <Ionicons name={t.icon} size={22} color={COMMUNITY_BLUE} />
                          <Text style={styles.templateBtnLabel}>{t.label}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                )}
                {cardBgPanelOpen && (
                  <View style={styles.panel}>
                    <View style={styles.panelTabRow}>
                      {['color', 'image', 'video'].map(tab => (
                        <TouchableOpacity
                          key={tab}
                          style={[styles.panelTab, cardBgTab === tab && { backgroundColor: COMMUNITY_BLUE }]}
                          onPress={() => setCardBgTab(tab)}
                        >
                          <Text style={[styles.panelTabText, cardBgTab === tab && { color: '#fff' }]}>
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                    {cardBgTab === 'color' && (
                      <CardBgColorPicker
                        swatches={CARD_BG_SWATCHES}
                        selectedColor={cardBackground?.type === 'color' ? cardBackground.value : null}
                        accentColor={COMMUNITY_BLUE}
                        onSelectColor={(color) => setCardBackground({ type: 'color', value: color })}
                      />
                    )}
                    {(cardBgTab === 'image' || cardBgTab === 'video') && (
                      <View style={styles.bgUploadRow}>
                        {cardBackground?.type === cardBgTab && cardBackground.value ? (
                          <View style={styles.bgPreviewWrap}>
                            {cardBgTab === 'image'
                              ? <Image source={{ uri: cardBackground.value }} style={styles.bgPreview} resizeMode="cover" />
                              : Platform.OS === 'web'
                                ? <video src={cardBackground.value} autoPlay loop muted playsInline style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 6 }} />
                                : <View style={[styles.bgPreview, { backgroundColor: '#222', alignItems: 'center', justifyContent: 'center' }]}><Ionicons name="videocam" size={28} color="#fff" /></View>
                            }
                            <TouchableOpacity onPress={() => setCardBackground(null)} style={styles.bgPreviewRemove}>
                              <Ionicons name="close-circle" size={20} color="rgba(0,0,0,0.6)" />
                            </TouchableOpacity>
                          </View>
                        ) : (
                          <TouchableOpacity
                            style={styles.bgUploadBtn}
                            onPress={() => handleCardBgUpload(cardBgTab)}
                            disabled={uploadingCardBg}
                          >
                            {uploadingCardBg
                              ? <ActivityIndicator size="small" color={COMMUNITY_BLUE} />
                              : <>
                                  <Ionicons name={cardBgTab === 'video' ? 'videocam-outline' : 'image-outline'} size={20} color={COMMUNITY_BLUE} />
                                  <Text style={styles.bgUploadBtnText}>Upload {cardBgTab}</Text>
                                </>
                            }
                          </TouchableOpacity>
                        )}
                      </View>
                    )}
                  </View>
                )}
              </View>
            )}
            <View style={{ zIndex: 1 }}>
              <EditablePostGrid
                items={items}
                setItems={setItemsWithHistory}
                widgetTypes={WIDGET_TYPES}
                onFileUpload={handleFileUpload}
                accentColor={COMMUNITY_BLUE}
                cellSize={CELL_SIZE}
                uploadingCellId={uploadingCellId}
                gridBgColor={cardBackground ? 'transparent' : '#fff'}
                interactionMode={gridMode}
              />
            </View>
            {/* <View style={styles.headerActions}>
              <TouchableOpacity
                onPress={handlePost}
                style={[styles.cardPostBtn, posting && styles.cardPostBtnDisabled]}
                disabled={posting}
                activeOpacity={0.8}
              >
                {posting ? (
                  <ActivityIndicator size="small" color={COMMUNITY_BLUE} />
                ) : (
                  <Text style={styles.cardPostBtnText}>{isEditing ? 'Update' : 'Post'}</Text>
                )}
              </TouchableOpacity>
            </View> */}
          </View>
        </View>

        {/* Primary post button below the card */}
        <View style={{ alignItems: 'center', marginTop: 24, marginBottom: 32 }}>
          {/* Admin-only checkbox — only shown to admin users */}
          {isUserAdmin && (
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 16 }}
              onPress={() => setAdminOnly((v) => !v)}
              activeOpacity={0.75}
            >
              <View style={{
                width: 20, height: 20, borderRadius: 4, borderWidth: 2,
                borderColor: adminOnly ? '#e74c3c' : '#aaa',
                backgroundColor: adminOnly ? '#e74c3c' : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}>
                {adminOnly && <Text style={{ color: '#fff', fontSize: 12, fontWeight: 'bold' }}>✓</Text>}
              </View>
              <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 13, color: '#e74c3c' }}>
                Admin only (hidden from public)
              </Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.postBtn, posting && styles.postBtnDisabled]}
            onPress={handlePost}
            disabled={posting}
            activeOpacity={0.8}
          >
            {posting ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <Text style={styles.postBtnText}>{isEditing ? 'UPDATE' : 'POST'}</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COMMUNITY_BLUE,
    paddingTop: Platform.OS === 'web' ? 80 : 70,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backBtn: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    fontFamily: 'Playpen Sans',
    fontSize: 20,
    fontWeight: '600',
    color: ACCENT,
    flex: 1,
  },
  postBtn: {
    paddingVertical: 10,
    paddingHorizontal: 32,
    borderRadius: 999,
    backgroundColor: '#000000',
    minWidth: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  postBtnDisabled: {
    opacity: 0.7,
  },
  postBtnText: {
    fontFamily: 'Playpen Sans',
    fontSize: 20,
    marginBottom: 0,
    color: '#ffffff',
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 40,
  },
  toolbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 8,
    minHeight: 35,
    alignItems: 'center',
  },
  toolbarLeft: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  modeBtn: {
    alignSelf: 'flex-end',
  },
  modeRow: {
    width: '100%',
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  modeAuxBtn: {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  modeAuxBtnDisabled: {
    opacity: 0.5,
  },
  modeAuxBtnTextDisabled: {
    color: '#9aa',
  },
  toolbarBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderWidth: 1,
    borderColor: COMMUNITY_BLUE,
  },
  toolbarBtnClear: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  toolbarBtnText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    color: COMMUNITY_BLUE,
  },
  gridWithPanelsWrap: {
    position: 'relative',
    zIndex: 1,
  },
  panelsOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 11,
    paddingBottom: 8,
  },
  panel: {
    backgroundColor: 'rgba(255,255,255,0.97)',
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
    maxWidth: 400,
    alignSelf: 'center',
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 6,
  },
  panelTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: '#555',
    marginBottom: 10,
  },
  panelTabRow: { flexDirection: 'row', gap: 6, marginBottom: 10 },
  panelTab: {
    paddingVertical: 5,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: '#f0f0f0',
  },
  panelTabText: { fontFamily: 'VastagoGrotesk', fontSize: 12, color: '#333' },
  templateRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  templateBtn: {
    alignItems: 'center',
    gap: 4,
    padding: 10,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    minWidth: 60,
  },
  templateBtnLabel: { fontFamily: 'VastagoGrotesk', fontSize: 10, color: '#333' },
  swatchGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  bgSwatch: { width: 32, height: 32, borderRadius: 16 },
  bgSwatchActive: { borderWidth: 3, borderColor: COMMUNITY_BLUE },
  hexRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  hexLabel: { fontFamily: 'VastagoGrotesk', fontSize: 14, color: '#555' },
  hexInput: {
    flex: 1,
    fontFamily: 'VastagoGrotesk',
    fontSize: 13,
    color: '#333',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
  hexApplyBtn: { paddingVertical: 5, paddingHorizontal: 12, backgroundColor: COMMUNITY_BLUE, borderRadius: 6 },
  hexApplyText: { fontFamily: 'VastagoGrotesk', fontSize: 12, color: '#fff' },
  bgUploadRow: { alignItems: 'flex-start' },
  bgUploadBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: COMMUNITY_BLUE,
    borderStyle: 'dashed',
  },
  bgUploadBtnText: { fontFamily: 'VastagoGrotesk', fontSize: 13, color: COMMUNITY_BLUE },
  bgPreviewWrap: { position: 'relative' },
  bgPreview: { width: 120, height: 80, borderRadius: 6 },
  bgPreviewRemove: { position: 'absolute', top: -8, right: -8 },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    maxWidth: 400,
    alignSelf: 'center',
    zIndex: 20,
    width: '100%',
    overflow: 'visible',
    position: 'relative',
  },
  cardBgLayer: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 12,
    overflow: 'hidden',
  },
  previewRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingBottom: 16,
  },
  previewInputs: {
    flex: 1,
  },
  previewPostInput: {
    fontFamily: 'Computer Says No',
    fontSize: 28,
    color: '#000',
    ...transparentTextInputStyle,
  },
  previewLocationInput: {
    fontFamily: 'Computer Says No',
    fontSize: 20,
    color: '#333',
    paddingVertical: 2,
    ...transparentTextInputStyle,
  },
  previewInterestStrip: {
    alignSelf: 'flex-end',
    marginTop: 2,
  },
  previewActionsCol: {
    alignItems: 'flex-end',
    gap: 4,
    flexShrink: 0,
  },
  headerActions: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 16,
    gap: 8,
  },
  folderBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
  },
  folderBtnLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    color: COMMUNITY_BLUE,
    marginTop: 4,
  },
  cardPostBtn: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: COMMUNITY_BLUE,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 72,
  },
  cardPostBtnDisabled: {
    opacity: 0.7,
  },
  cardPostBtnText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  gridOuter: {
    borderWidth: 1,
    borderColor: 'transparent',
    borderRadius: 4,
    // overflow: 'hidden',
    backgroundColor: '#fff',
    width: GRID_COLS * CELL_SIZE,
    alignSelf: 'center',
  },
  gridRow: {
    flexDirection: 'row',
  },
  gridRowLast: {
    borderBottomWidth: 0,
  },
  gridCell: {
    minHeight: 0,
    backgroundColor: '#fff',
    position: 'relative',
    boxShadow:`0px 0px 0px 0.5px rgba(0, 0, 0, 0.2) inset`,
  },
  gridCellSelected: {
    boxShadow:`0px 0px 0px 1px ${COMMUNITY_BLUE} inset`,
  },
  gridCellLastInRow: {
    borderRightWidth: 0,
  },
  gridCellLastRow: {
    borderBottomWidth: 0,
  },
  gridCellEmpty: {
    width: CELL_SIZE,
  },
  gridCellCoveredPlaceholder: {
    backgroundColor: 'transparent',
  },
  gridCellOverlay: {
    width: 140,
    minHeight: 52,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 6,
    padding: 8,
  },
  gridCellInner: {
    flex: 1,
    minHeight: 0,
    padding: 6,
    position: 'relative',
  },
  pickerBackdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  pickerPositionWrap: {
    position: 'absolute',
    alignItems: 'center',
  },
  widgetPickerWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '100%',
    marginBottom: 4,
    zIndex: 10,
    alignItems: 'center',
  },
  widgetPicker: {
    alignSelf: 'center',
  },
  widgetPickerContent: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 6,
    gap: 4,
    borderWidth: 1,
    borderColor: '#ccc',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 6,
    elevation: 4,
  },
  widgetPickerItem: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    backgroundColor: '#f5f5f5',
  },
  widgetPickerItemActive: {
    backgroundColor: COMMUNITY_BLUE,
  },
  widgetPickerLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 8,
    color: '#333',
  },
  widgetPickerLabelActive: {
    color: '#fff',
  },
  widgetPickerMergeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 6,
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mergeDropdownWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  mergeDropdownTriggerWrap: {
    position: 'relative',
  },
  mergeDropdownLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: '#333',
  },
  mergeDropdownTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#b0b0b0',
    borderRadius: 6,
    backgroundColor: '#fff',
    minWidth: 100,
  },
  mergeDropdownTriggerText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: COMMUNITY_BLUE,
    flex: 1,
  },
  mergeDropdownList: {
    position: 'absolute',
    top: 38,
    left: 0,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#b0b0b0',
    borderRadius: 6,
    minWidth: 100,
    zIndex: 10,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
  },
  mergeDropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  widgetPickerMergeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  widgetPickerMergeLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    color: COMMUNITY_BLUE,
  },
  gridCellBody: {
    flex: 1,
    minHeight: 32,
    justifyContent: 'center',
  },
  gridInput: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    color: '#000',
    padding: 4,
    borderWidth: 1,
    borderColor: '#eee',
    borderRadius: 4,
    minHeight: 28,
  },
  gridInputPlaceholder: {
    minHeight: 28,
    borderWidth: 1,
    borderColor: '#eee',
    borderRadius: 4,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  gridMediaFilled: {
    borderStyle: 'solid',
    padding: 0,
    flex: 1,
    alignSelf: 'stretch',
  },
  gridMediaImage: {
    width: '100%',
    height: '100%',
    minHeight: 40,
  },
  gridMediaVideoWrap: {
    flex: 1,
    width: '100%',
    minHeight: 40,
    backgroundColor: '#333',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  gridMediaVideoLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 9,
    color: 'rgba(255,255,255,0.9)',
    marginTop: 2,
  },
  gridInputPlaceholderText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
    color: '#999',
    marginTop: 2,
  },
  gridInputPlaceholderSubtext: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 9,
    color: '#bbb',
    marginTop: 1,
  },
  gridCellBodyText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    color: '#333',
  },
});
